<template>
  <div class="water-sample-series">
    <div class="series-panel">
      <div class="series-panel-title">{{ $t('label.measurementSeries') }}</div>

      <p v-if="!loading && !seriesFields.length" class="series-empty">{{ $t('message.noSeriesFieldsInTemplate') }}</p>

      <div
        v-if="seriesFields.length"
        class="series-scroll"
        :class="{ 'is-scrolled-sideways': isScrolledSideways }"
        @scroll="onScroll">
        <table class="series-table">
          <thead>
            <tr>
              <th class="series-field-cell"></th>
              <th
                v-for="entry in series"
                :key="entry.ObSerieTimerValue"
                class="series-entry-cell"
                :class="{ 'is-active': entry.ObSerieTimerValue === activeTimerValue }">
                <span class="series-sequence-number">{{ entry.ObSerieTimerValue }}.</span>
                <input
                  v-if="hasMeasurements(entry)"
                  type="time"
                  step="1"
                  class="series-time"
                  :value="entry.time"
                  @change="setTime(entry, $event.target.value)">
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="seriesField in seriesFields" :key="seriesField.key">
              <th class="series-field-cell" :title="fieldLabel(seriesField)">{{ fieldLabel(seriesField) }}</th>
              <td
                v-for="entry in series"
                :key="entry.ObSerieTimerValue"
                class="series-entry-cell"
                :class="{ 'is-active': entry.ObSerieTimerValue === activeTimerValue }">
                <template v-if="!isLocked(entry, seriesField)">
                  <select
                    v-if="seriesField.isCode"
                    class="form-control"
                    :value="entry.values[seriesField.key] || ''"
                    @change="setValue(entry, seriesField, $event.target.value)">
                    <option value=""></option>
                    <option v-for="option in seriesField.options" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                  <input
                    v-else
                    type="number"
                    class="form-control"
                    :value="entry.values[seriesField.key] || ''"
                    @change="setValue(entry, seriesField, $event.target.value)">
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <nv-loading :show="loading"></nv-loading>
  </div>
</template>

<script>
'use strict'

import VueCookie from 'vue-cookie'
import nvLoading from '@/components/_shared/loading.vue'
import waterSampleSeriesUtils from '@/utils/waterSampleSeriesUtils'
import { TemplateService } from '@/services/DataWS/templateService'
import { WATER_SAMPLE_TYPE } from '@/vuex/constants'

const templateService = new TemplateService()

export default {
  name: 'WaterSampleSeriesTable',

  components: {
    nvLoading
  },

  props: {
    waterSample: {
      type: Object,
      required: true
    },
    observations: {
      type: Array,
      default: () => []
    },
    projectId: {
      type: String,
      required: true
    },
    sampleType: {
      type: String,
      default: WATER_SAMPLE_TYPE
    }
  },

  data () {
    return {
      loading: true,
      seriesFields: [],
      series: [],
      stableFieldKeys: [],
      isScrolledSideways: false
    }
  },

  computed: {
    activeTimerValue () {
      return waterSampleSeriesUtils.getActiveTimerValue(this.series, this.seriesFields)
    },
    screen () {
      return waterSampleSeriesUtils.getSeriesScreen(this.sampleType)
    }
  },

  watch: {
    'waterSample.WsGuid' () {
      this.buildSeries()
    },
    observations () {
      this.buildSeries()
    },
    screen () {
      this.loadTemplate()
    }
  },

  mounted () {
    this.loadTemplate()
  },

  methods: {
    loadTemplate () {
      this.loading = true

      return templateService
        .getFieldTemplate(this.screen, this.projectId, null)
        .then(template => {
          this.seriesFields = waterSampleSeriesUtils.getSeriesFields(template, VueCookie.get('language'), this.sampleType)
          this.buildSeries()
        })
        .catch(error => {
          console.error(error)
          this.seriesFields = []
        })
        .then(() => {
          this.loading = false
        })
    },

    buildSeries () {
      this.series = waterSampleSeriesUtils.buildSeries(this.observations, this.waterSample, this.seriesFields)
      this.stableFieldKeys = waterSampleSeriesUtils.getSeriesFieldKeys(this.series, this.seriesFields)
    },

    isLocked (entry, seriesField) {
      return waterSampleSeriesUtils.isLocked(this.series, entry, seriesField)
    },

    onScroll (event) {
      this.isScrolledSideways = event.target.scrollLeft > 0
    },

    // A row reads like the field in the editor panel next to it. Only where one control holds several
    // series fields would that leave rows with the same name, and those fields have a name of their
    // own in the translations.
    fieldLabel (seriesField) {
      if (seriesField.sharesControlLabel && this.$te(seriesField.labelTranslationKey)) {
        return this.$t(seriesField.labelTranslationKey)
      }

      return seriesField.label
    },

    setValue (entry, seriesField, value) {
      this.$set(entry.values, seriesField.key, value)
      waterSampleSeriesUtils.applyTimeStamp(entry, this.seriesFields)

      if (!this.stableFieldKeys.includes(seriesField.key)) this.stableFieldKeys.push(seriesField.key)

      this.emitChange()
    },

    hasMeasurements (entry) {
      return waterSampleSeriesUtils.hasMeasurements(entry, this.seriesFields)
    },

    setTime (entry, value) {
      entry.time = value && value.length === 5 ? `${value}:00` : value
      this.emitChange()
    },

    emitChange () {
      this.$emit('change', {
        observations: waterSampleSeriesUtils.toObservations(this.series, this.seriesFields, this.waterSample, this.projectId),
        stableValues: waterSampleSeriesUtils.getStableValues(this.series, this.seriesFields, this.stableFieldKeys)
      })
    }
  }
}
</script>

<style scoped lang="less">
  .water-sample-series {
    box-sizing: border-box;
    height: calc(100vh - 120px);
    padding: 5px 10px 0;
    overflow: hidden;
  }

  .series-panel {
    display: flex;
    flex-direction: column;
    width: fit-content;
    max-width: 100%;
    max-height: 100%;
    background: transparent;
  }

  .series-panel-title {
    padding: 5px 10px;
    border-bottom: 1px solid @gray200;
    font-size: 14px;
    font-weight: 500;
    line-height: 25px;
    color: @black;
  }

  .series-empty {
    margin: 0;
    padding: 10px;
    color: @gray500;
  }

  .series-scroll {
    min-height: 0;
    overflow: auto;
    background-color: @white;

    &::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    &::-webkit-scrollbar-thumb {
      border: 3px solid @white;
      border-radius: 5px;
      background: @gray300;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: @gray400;
    }
  }

  .series-table {
    table-layout: fixed;
    width: max-content;
    border-collapse: separate;
    border-spacing: 0;

    th, td {
      padding: 3px 5px;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      height: 30px;
      background: @gray50;
      border-bottom: 1px solid @gray300;
      font-weight: 400;
      text-align: left;
      white-space: nowrap;
    }

    thead .series-field-cell {
      z-index: 3;
      background: @gray50;
    }

    tbody th,
    tbody td {
      border-bottom: 1px solid @gray200;
    }

    tbody tr:hover th,
    tbody tr:hover td {
      background: @gray50;
    }

    .series-entry-cell.is-active {
      background: @greenTerra25;
    }

    thead .series-entry-cell.is-active {
      background: @greenTerra50;
    }

    .form-control {
      height: 30px;
      padding: 4px 8px;
      border-radius: 0;
    }
  }

  .series-field-cell {
    position: sticky;
    left: 0;
    z-index: 1;
    width: 180px;
    padding-right: 10px;
    border-right: 1px solid @gray300;
    background: @white;
    font-weight: 500;
    color: @black;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .series-scroll.is-scrolled-sideways .series-field-cell {
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.18);
  }

  .series-entry-cell {
    width: 130px;
  }

  .series-sequence-number {
    margin-right: 6px;
    color: @gray500;
  }

  .series-time {
    width: auto;
    padding: 0;
    border: none;
    background: transparent;
    font-size: 13px;
    color: @gray500;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
</style>
